import { useState, useEffect, Dispatch, SetStateAction } from "react";

import { css, keyframes } from "@compiled/react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs,
    useAdg4TabsContext
} from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import Video from "@/components/wpl/video/src";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";
import { UserSegmentationTeamTypes } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/models/user-segmentation";
import { useAnchorLocalStorage, TeamType } from "@/utils/local-storage-hook";

const Tab = ({
    tabData,
    tabIndex,
    setSelectedTeam
}: {
    tabData: (typeof tailoredOnboardingTabsData)[0];
    tabIndex: number;
    setSelectedTeam: Dispatch<SetStateAction<TeamType>>;
}) => {
    const { currentTab } = useAdg4TabsContext();
    const isSelected = currentTab === tabIndex;

    return (
        <Button
            variant="primary_alt"
            color="black"
            data-testid={`team-tab-${tabIndex}`}
            role="tab"
            css={[buttonStyle, isSelected && selectedButtonStyle]}
            onClick={() => setSelectedTeam(tabData.team)}
            dataEventProps={{
                "data-event": "clicked",
                "data-event-component": "button",
                "data-event-container": "homepageTailoredOnboarding",
                "data-product-key": "home",
                "data-label": `${tabData.team || "default"} tab`,
                "data-track-event": "homepageTailoredOnboarding.teamTabClicked"
            }}
        >
            <Picture
                image={{
                    src: tabData.tabIconSrc,
                    alt: "",
                    height: 26
                }}
                css={iconStyle}
            />
            <FormattedMessage {...tabData.tabText} />
        </Button>
    );
};

export const HomepageTailoredOnboarding = () => {
    const intl = useIntl();
    const [selectedTeam, setSelectedTeam] = useState<TeamType>("");
    const { setAnchorLocalStorage } = useAnchorLocalStorage();
    const { addTeamTypeCallback } = useGupsStatus();

    useEffect(() => {
        setAnchorLocalStorage({
            team: selectedTeam
        });
    }, [selectedTeam, setAnchorLocalStorage]);

    return (
        <UFOSegment name="home-page-tailored-onboarding">
            <div css={sectionStyle}>
                <section
                    id="tailored-onboarding"
                    data-testid="tailored-onboarding"
                >
                    <Adg4Tabs>
                        <div>
                            <Adg4DraggableTabList
                                css={tabListStyles}
                                rowGap="12px"
                            >
                                {tailoredOnboardingTabsData.map((item, i) => (
                                    <Adg4DraggableTab key={i}>
                                        <Tab
                                            tabData={item}
                                            tabIndex={i}
                                            setSelectedTeam={setSelectedTeam}
                                        />
                                    </Adg4DraggableTab>
                                ))}
                            </Adg4DraggableTabList>
                        </div>
                        <Row layout={"12"}>
                            <>
                                {tailoredOnboardingTabsData.map((item, i) => (
                                    <Adg4TabPanel key={i}>
                                        <div css={panelWrapperStyle}>
                                            <div css={panelContainerLeftStyle}>
                                                <div
                                                    css={teamTypeContainerStyle}
                                                >
                                                    <h2 css={teamTypeStyle}>
                                                        <FormattedMessage
                                                            id="HomePageTemplateV2.tailoredOnboarding.tab.jiraStart"
                                                            defaultMessage="{tabText}{newline}starts with Jira"
                                                            values={{
                                                                tabText: (
                                                                    <FormattedMessage
                                                                        {...item.highlightedPanelHeader}
                                                                    />
                                                                ),
                                                                newline: <br />
                                                            }}
                                                            description={
                                                                "String describing Jira capabilities. The newline is used to put the team type (ex. 'Operations') on its own line in English."
                                                            }
                                                        />
                                                    </h2>
                                                </div>
                                                <p css={subtextStyle}>
                                                    <FormattedMessage
                                                        {...item.panelSubText}
                                                    />
                                                </p>
                                                <Button
                                                    css={buttonCTAStyle}
                                                    dataEventProps={{
                                                        "data-event": "clicked",
                                                        "data-event-component":
                                                            "linkButton",
                                                        "data-event-container":
                                                            "homepageTailoredOnboarding",
                                                        "data-product-key":
                                                            "home",
                                                        "data-label": `${
                                                            item.team ||
                                                            "default"
                                                        } cta button`,
                                                        "data-track-event":
                                                            "homepageTailoredOnboarding.ctaClicked"
                                                    }}
                                                    href="/try/cloud/signup?bundle=jira-software&edition=free"
                                                    onClick={() =>
                                                        item.team &&
                                                        addTeamTypeCallback(
                                                            item.team.toLowerCase() as UserSegmentationTeamTypes
                                                        )
                                                    }
                                                >
                                                    <FormattedMessage
                                                        {...item.panelCtaText}
                                                    />
                                                </Button>
                                            </div>
                                            <div css={panelContainerRightStyle}>
                                                {item.panelVideo ? (
                                                    <Video
                                                        videoLink={
                                                            item.panelVideo.src
                                                        }
                                                        videoPreviewImage={
                                                            item.panelVideo
                                                                .previewImg
                                                        }
                                                        loop={false}
                                                        css={videoPhotoStyle}
                                                    />
                                                ) : (
                                                    item.panelImg && (
                                                        <Picture
                                                            image={{
                                                                src: item
                                                                    .panelImg
                                                                    .src,
                                                                alt: intl.formatMessage(
                                                                    item
                                                                        .panelImg
                                                                        .alt
                                                                ),
                                                                height: 662,
                                                                width: 1000
                                                            }}
                                                            style={{
                                                                maxWidth:
                                                                    "100%",
                                                                width: "100%",
                                                                height: "auto"
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </Adg4TabPanel>
                                ))}
                            </>
                        </Row>
                    </Adg4Tabs>
                </section>
            </div>
        </UFOSegment>
    );
};

const sectionStyle = css({
    paddingBlockStart: 20,
    paddingBlockEnd: 80,
    [media.below.sm]: {
        paddingBlockStart: 64
    }
});

const tabListStyles = css({
    paddingInlineStart: 60,
    paddingInlineEnd: 60,
    paddingBlockEnd: 48,

    [media.below.sm]: {
        paddingBlockEnd: 24,
        paddingInlineStart: 32,
        paddingInlineEnd: 32
    }
});

const buttonStyle = css({
    "border": "2px solid transparent",
    "backgroundColor": "#F8F8F8",
    "minHeight": 48,
    "fontFamily": wacToken("font.charlie.display"),
    "fontWeight": 500,
    "fontSize": 20,
    "marginBlockEnd": 1,

    "&:hover": {
        // wac token does not work for button background color

        backgroundColor: "#F8F8F8",
        boxShadow: token("elevation.shadow.raised")
    }
});

const selectedButtonStyle = css({
    border: `2px solid ${wacToken("color.primary.blue")}`,
    // wac token does not work for button background color

    backgroundColor: "#E9F2FE !important"
});

const iconStyle = css({
    marginInlineEnd: 4,
    position: "relative",
    insetBlockStart: 1
});

const videoPhotoStyle = css({
    maxWidth: "100%",
    width: "100%",
    height: "auto",
    display: "block",
    margin: 0,
    padding: 0,
    clipPath: "inset(1px 1px)"
});

const teamTypeContainerStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    marginBlockEnd: "20px",
    alignItems: "flex-start",
    [media.below.sm]: {
        alignItems: "center",
        marginBlockEnd: "10px"
    }
});

const fadein = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const panelWrapperStyle = css({
    [media.below.md]: {
        display: "block"
    },
    animation: `${fadein} linear 500ms 1 forwards`,
    display: "flex",
    gap: 20
});

const panelContainerLeftStyle = css({
    flex: "1 1 50%",
    display: "flex",
    flexDirection: "column",

    [media.below.md]: {
        alignItems: "center"
    }
});

const panelContainerRightStyle = css({
    flex: "1 1 50%",
    [media.below.md]: {
        paddingBlockStart: 48
    }
});

const teamTypeStyle = css({
    marginBlock: "0px",
    padding: "0px 20px 9px 15px",
    fontSize: "70px !important",
    fontStyle: "normal",
    fontWeight: "775 !important",
    lineHeight: "80px !important",
    [media.below.sm]: {
        fontSize: "50px !important",
        lineHeight: "60px !important",
        marginInlineStart: "20px",
        textAlign: "center"
    }
});

const subtextStyle = css({
    margin: 0,
    fontSize: "20px !important",
    lineHeight: "28px !important",
    marginBlockEnd: "32px",
    marginInlineStart: "10px",
    fontWeight: "400 !important",
    [media.below.sm]: {
        textAlign: "center"
    }
});

const buttonCTAStyle = css({
    marginInlineStart: "10px",
    width: "fit-content",

    [media.below.sm]: {
        display: "block",
        textAlign: "center",
        marginInlineStart: "0",
        width: "fit-content"
    }
});

export const tailoredOnboardingTabsData: TabsDataInterface[] = [
    {
        // blank string is used for default team type value
        team: "",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/0e62epv43p2037hf1y6mpjn63k4027cx.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab1.tabText",
            defaultMessage: "All teams"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab1.highlitedPanelHeader",
            defaultMessage: "Team success"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab1.panelSubText",
            defaultMessage:
                "The only tool your team needs to plan and track work."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab1.panelCtaText",
            defaultMessage: "Get started free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelVideo: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/sjjc8k61bt08n608k5hlutfd3xapk48l.mp4",
            previewImg:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/658m24w7wy4w643oow72d7t7hmat68t5.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab1.panelVdAlt",
                defaultMessage:
                    "This is a video of a to-do ticket on a Jira board being edited and a ticket being dragged from in review to done."
            })
        }
    },
    {
        team: "Marketing",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/22p021mt32j31e1rb4861r1v521r0g0u.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab2.tabText",
            defaultMessage: "Marketing"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab2.highlitedPanelHeader",
            defaultMessage: "Marketing"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab2.panelSubText",
            defaultMessage:
                "From simple tasks to complex projects, manage marketing the smart way - with Jira."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab2.panelCtaText",
            defaultMessage: "Get Jira for Marketing free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelImg: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7stxu0as4vhbl8a7y704cx600407x3fc.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab2.panelImgAlt",
                defaultMessage:
                    "This is an image of Marketing team setting a Jira epic to on track."
            })
        }
    },
    {
        team: "Software Engineering",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/077n3d25i15ei74w8j0orf15jfdsg7r4.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab3.tabText",
            defaultMessage: "Engineering"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab3.highlitedPanelHeader",
            defaultMessage: "Engineering"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab3.panelSubText",
            defaultMessage:
                "Build great software with simplified planning, tracking, and release management."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab3.panelCtaText",
            defaultMessage: "Get Jira for Engineering free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelImg: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/2a50t076ayl168w5gfbbf5o77834b3c7.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab3.panelImgAlt",
                defaultMessage:
                    "This is an image of a Jira ticket being dragged from in progress to done by an Engineer."
            })
        }
    },
    {
        team: "Design",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/64wphwx7cxsk1tln85ol827hv7d253l6.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab4.tabText",
            defaultMessage: "Design"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab4.highlitedPanelHeader",
            defaultMessage: "Design"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab4.panelSubText",
            defaultMessage:
                "Align with product, share designs, and meet deadlines - all without sacrificing creativity."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab4.panelCtaText",
            defaultMessage: "Get Jira for Design free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelImg: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/s16vwa08laqgd4045o343155ux8g8m7w.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab4.panelImgAlt",
                defaultMessage:
                    "This is an image of a Jira ticket being marked as ready for dev by a Designer."
            })
        }
    },
    {
        team: "Operations",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/e1we7gna82f185x4877hy4sk3m0exnjt.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab5.tabText",
            defaultMessage: "Operations"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab5.panelHeader",
            defaultMessage: "Operations"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab5.panelSubText",
            defaultMessage:
                "From control processes to resource management, conquer complex workflows and quickly deliver results - with Jira."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab5.panelCtaText",
            defaultMessage: "Get Jira for Operations free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelImg: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/kkym8cjo15d2745w444q84ii7r4c616c.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab5.panelImgAlt",
                defaultMessage:
                    "This is an image of work being linked to a goal by an Operations Manager."
            })
        }
    },
    {
        team: "Customer Service",
        tabIconSrc:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/xim145006ni54kwkp7i5bn31go457s42.svg",
        tabText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab6.tabText",
            defaultMessage: "IT"
        }),
        highlightedPanelHeader: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab6.panelHeader",
            defaultMessage: "IT"
        }),
        panelSubText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab6.panelSubText",
            defaultMessage:
                "From issue tracking to change management, streamline IT projects and deliver reliable services to internal teams."
        }),
        panelCtaText: defineMessage({
            id: "HomePageTemplateV2.tailoredOnboarding.tab6.panelCtaText",
            defaultMessage: "Get Jira for IT free"
        }),
        panelCtaHref: "/try/cloud/signup?bundle=jira-software&edition=free",
        panelImg: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/3m2ramre5k810mu1k7dwg248f0tfa4ha.webp",
            alt: defineMessage({
                id: "HomePageTemplateV2.tailoredOnboarding.tab6.panelImgAlt",
                defaultMessage:
                    "This is an image of a Jira ticket being dragged from in progress to done by IT."
            })
        }
    }
];

interface TabsDataInterface {
    team: TeamType;
    tabIconSrc: string;
    tabText: {
        id: string;
        defaultMessage: string;
    };
    highlightedPanelHeader: {
        id: string;
        defaultMessage: string;
    };
    panelSubText: {
        id: string;
        defaultMessage: string;
    };
    panelCtaText: {
        id: string;
        defaultMessage: string;
    };
    panelCtaHref: string;
    panelImg?: {
        src: string;
        alt: {
            id: string;
            defaultMessage: string;
        };
    };
    panelVideo?: {
        src: string;
        previewImg: string;
        alt: {
            id: string;
            defaultMessage: string;
        };
    };
}

HomepageTailoredOnboarding.displayName = "HomepageTailoredOnboarding";
